import { SearchOutlined } from '@ant-design/icons';
import { flatten, isEqual, snakeCase, uniqBy } from 'lodash';
import type { BaseSelectRef } from 'rc-select';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAssetColumnCopy } from 'components/data/assets/assetColumnRenderers';
import { None } from 'components/data/helpers/helpers';
import { DataTableContext } from 'components/DataTable';
import {
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from 'components/DataTable/customFilters/TableFilterMultiSelect';
import { Button, Tooltip, TooltipWithQuestionIcon } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import type { CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import type {
  AtlasGqlAsset,
  AtlasGqlAssetFieldValueSelect,
  AtlasGqlAssetType,
  AtlasGqlCombinationOption,
} from 'types/atlas-graphql';
import {
  AtlasGqlFilterOperator,
  useGetAssetsForAssetFilterQuery,
  useGetComponentsForAssetFilterQuery,
  useGetMakeModelOptionsLazyQuery,
  useGetTypesForAssetFilterQuery,
} from 'types/atlas-graphql';
import { naturalSortBy } from 'utils/naturalSort';

import {
  ASSET_FILTER_MAX_HEIGHT_PX,
  AssetFilterActions,
  AssetFilterInnerContainer,
  AssetFilterPopover,
  AssetFilterWarning,
  AssetSelectContainer,
  ComponentFiltersCollapse,
} from './AssetFilter.style';
import {
  type SelectedMakeModel,
  ASSET_MAKE_MODEL_PAIRS_KEY,
  assetMatchesAssetTypes,
  buildMakeOptionsFromAssets,
  buildModelsByMakeFromAssets,
  buildModelsByMakeFromCombinations,
  consumeSiteFilterChange,
  decodeMakeModelPairs,
  DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP,
  encodeMakeModelPairs,
  MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS,
  makeModelFilterKeys,
  MakeModelNestedFilter,
  matchesMakeModelSelection,
} from './MakeModelNestedFilter';
import { filterAssetsByTypes, retainSelectedAssetsForTypeChange } from './utils';

const searchSuffixIcon = <SearchOutlined aria-hidden />;

/**
 * lockedAssetTypes - the list of asset types pre-selected due to being on the asset
 *                    details page
 * lockedAssetIds - the list of asset ids pre-selected due to being on the asset
 *                  details page
 * stateToGraphqlKeyMap - mapping of state keys of this component to graphql filter keys.
 *                  A default is provided that matches what is used by the damages service.
 *                  Provide this prop as necessary for other tables/services
 *
 * When lockedAssetTypes or lockedAssetIds are provided, the asset section will be
 * automatically populated and disabled, allowing for further filtering by component,
 * where applicable (e.g. if on the asset details page for a turbine, the turbine type
 * and specific turbine will be selected, and the user can filter by child component).
 * With lockedAssetIds, the asset Make/Model filter is hidden.
 */
type AssetFilterProps = CustomFilterDropdownProps & {
  tableId: string;
  lockedAssetTypes?: AtlasGqlAssetType[];
  lockedAssetIds?: string[];
  stateToGraphqlKeyMap?: StateToGraphqlKeyMap;
  /** Nested Make/Model tree + pair keys. Default false keeps separate Make/Model selects. */
  enableNestedMakeModelFilter?: boolean;
  /**
   * Also drop the selected Asset/Component types when Site changes, for tables that reset the
   * whole Asset column filter. Default false keeps the types so only Make/Model is re-picked.
   */
  resetTypesOnSiteChange?: boolean;
};
type StateToGraphqlKeyMap = {
  assetTypes: string;
  assetMakes: string;
  assetModels: string;
  assets: string;
  componentTypes: string;
  componentMakes: string;
  componentModels: string;
  components: string;
};
export const DefaultStateToGraphqlKeyMap = {
  assetTypes: 'assetType',
  ...DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP,
  assets: 'assetId',
  componentTypes: 'componentType',
  components: 'componentId',
};

type AssetFilterState = {
  assetTypes?: AtlasGqlAssetType[];
  assetMakes?: string[];
  assetModels?: string[];
  assetMakeModelPairs?: SelectedMakeModel[];
  assets?: AtlasGqlAsset[];
  componentTypes?: AtlasGqlAssetType[];
  componentMakes?: string[];
  componentModels?: string[];
  components?: AtlasGqlAsset[];
};

const assetFilterStateReducer = (
  prevState: AssetFilterState,
  incomingState: AssetFilterState
): AssetFilterState => ({
  ...prevState,
  ...incomingState,
});

const NONE_OPTION = { label: <None />, value: '' };

const FIRST_PAGE_SIZE = 100;
const SUBSEQUENT_PAGE_SIZE = 500;
const ASSET_LIMIT = 1500;
const COMPONENT_LIMIT = 1500;

const filterModelOptionsBySelectedMakes = (
  modelOptionsToFilter: string[] | null | undefined,
  selectedMakes: string[] | null | undefined,
  independentCombinationOptions: AtlasGqlCombinationOption[] | null | undefined
): {
  value: string;
}[] =>
  uniqBy(
    flatten(
      independentCombinationOptions
        ?.filter(
          ({ independentFieldValue }) =>
            independentFieldValue && selectedMakes?.includes(independentFieldValue)
        )
        ?.map(({ dependentFieldOptions }) => dependentFieldOptions?.map(o => ({ value: o })))
    ).filter((o): o is { value: string } => !!o && (modelOptionsToFilter ?? []).includes(o.value)),
    'value'
  );

export const AssetFilter: React.FunctionComponent<AssetFilterProps> = ({
  tableId,
  stateToGraphqlKeyMap = DefaultStateToGraphqlKeyMap,
  lockedAssetTypes,
  lockedAssetIds,
  enableNestedMakeModelFilter = false,
  resetTypesOnSiteChange = false,
  setSelectedKeys,
  confirm,
  clearFilters,
}) => {
  const { t } = useTranslation();

  const { filteredInfo } = useContext(DataTableContext);
  // antd 5 does not re-render a closed filter dropdown, so its selectedKeys prop stays stale when a
  // saved view or reset changes this column while it is closed. The table context still reaches it,
  // and this filter sets selectedKeys only as it confirms, so the column's applied filter is the
  // value antd 4 passed as selectedKeys.
  const appliedAssetFilter = JSON.stringify(filteredInfo?.asset ?? []);
  const selectedKeys: CustomFilterDropdownProps['selectedKeys'] = useMemo(
    () => JSON.parse(appliedAssetFilter),
    [appliedAssetFilter]
  );

  // Invert the stateToGraphqlKeyMap prop for convenient mapping in either direction
  const graphqlToStateKeyMap: {
    [key: string]: keyof StateToGraphqlKeyMap;
  } = Object.fromEntries(
    Object.entries(stateToGraphqlKeyMap).map(([k, v]: [keyof StateToGraphqlKeyMap, string]) => [
      v,
      k,
    ])
  );

  const inputRef = useRef<BaseSelectRef>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [filterState, dispatchFilterState] = useReducer<
    (prevState: AssetFilterState, incomingState: AssetFilterState) => AssetFilterState
  >(assetFilterStateReducer, {});
  const {
    assetTypes,
    assetMakes,
    assetModels,
    assetMakeModelPairs,
    assets,
    componentTypes,
    componentMakes,
    componentModels,
    components,
  } = filterState;
  const [allAssets, setAllAssets] = useState<AtlasGqlAsset[]>([]);
  const [allComponents, setAllComponents] = useState<AtlasGqlAsset[]>([]);
  const [persistedStateRestored, setPersistedStateRestored] = useState<boolean>(false);
  const [isComponentCollapseOpen, setIsComponentCollapseOpen] = useState<boolean>(() => {
    const componentGraphqlKeys = new Set([
      stateToGraphqlKeyMap['componentTypes'],
      stateToGraphqlKeyMap['componentMakes'],
      stateToGraphqlKeyMap['componentModels'],
      stateToGraphqlKeyMap['components'],
    ]);
    return (selectedKeys as { key: string }[]).some(k => componentGraphqlKeys.has(k.key));
  });

  const selectedSiteIds: string[] = useMemo(
    () => filteredInfo?.site ?? [],
    [JSON.stringify(filteredInfo?.site)]
  );
  const sitesSelected = !!selectedSiteIds.length;

  const { data: assetTypesData, loading: assetTypesLoading } = useGetTypesForAssetFilterQuery();
  const [
    getAssetMakeModelOptions,
    { data: assetMakeModelData, loading: assetMakeModelDataLoading },
  ] = useGetMakeModelOptionsLazyQuery();
  const [
    getComponentMakeModelOptions,
    { data: componentMakeModelData, loading: componentMakeModelDataLoading },
  ] = useGetMakeModelOptionsLazyQuery();

  /** asset data query **********/
  const {
    data: assetsData,
    loading: assetsLoading,
    fetchMore: fetchMoreAssets,
  } = useGetAssetsForAssetFilterQuery({
    variables: {
      filterBy: [
        ...(lockedAssetIds?.length
          ? [
              {
                key: 'assetIds',
                values: lockedAssetIds,
                operator: AtlasGqlFilterOperator.Equals,
              },
            ]
          : [
              {
                key: 'descendantOfId',
                values: selectedSiteIds,
              },
            ]),
        ...(assetTypes?.length
          ? [
              {
                key: 'assetTypeIds',
                values: assetTypes.map(({ id }) => id),
              },
            ]
          : []),
      ],
      limit: FIRST_PAGE_SIZE,
      offset: 0,
    },
    notifyOnNetworkStatusChange: true,
    skip: (!sitesSelected || !assetTypes?.length) && !lockedAssetIds?.length,
  });

  // check to see if total assets exceeed the ASSET_LIMIT
  const tooManyAssets = useMemo(
    () => (assetsData?.assetsWithMetadata.totalCount ?? 0) > ASSET_LIMIT,
    [assetsData?.assetsWithMetadata?.totalCount]
  );

  /**
   * Set allAssets state when the initial query responds, then progressively fetch
   * subsequent pages and merge them into state.
   */
  useEffect(() => {
    if (!assetsData?.assetsWithMetadata?.items) {
      return;
    }

    const initialItems = assetsData.assetsWithMetadata.items.filter((a): a is AtlasGqlAsset => !!a);
    const totalCount = assetsData.assetsWithMetadata.totalCount;
    setAllAssets(initialItems);

    if (initialItems.length >= totalCount || initialItems.length >= ASSET_LIMIT) {
      return;
    }

    let cancelled = false;
    (async () => {
      let accumulated = initialItems;
      let nextOffset = assetsData.assetsWithMetadata.items.length;
      while (accumulated.length < totalCount && accumulated.length < ASSET_LIMIT) {
        const limit = Math.min(SUBSEQUENT_PAGE_SIZE, ASSET_LIMIT - accumulated.length);
        const result = await fetchMoreAssets({
          variables: { limit, offset: nextOffset },
        });
        if (cancelled) {
          return;
        }
        const rawPage = result.data?.assetsWithMetadata?.items ?? [];
        if (!rawPage.length) {
          break;
        }
        const newItems = rawPage.filter((a): a is AtlasGqlAsset => !!a);
        nextOffset += rawPage.length;
        accumulated = [...accumulated, ...newItems];
        setAllAssets(accumulated);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsData]);

  /** component data query **********/
  const {
    data: componentsData,
    loading: componentsLoading,
    fetchMore: fetchMoreComponents,
  } = useGetComponentsForAssetFilterQuery({
    variables: {
      filterBy: [
        {
          key: 'descendantOfId',
          values: assets?.map(({ id }) => id) ?? [],
        },
      ],
      limit: FIRST_PAGE_SIZE,
      offset: 0,
    },
    notifyOnNetworkStatusChange: true,
    skip: !isComponentCollapseOpen || !assets?.length,
  });

  // check to see if total components exceeed the COMPONENT_LIMIT
  const tooManyComponents = useMemo(
    () => (componentsData?.assetsWithMetadata.totalCount ?? 0) > COMPONENT_LIMIT,
    [componentsData?.assetsWithMetadata?.totalCount]
  );

  /**
   * Set allComponents state when the initial query responds, then progressively fetch
   * subsequent pages and merge them into state.
   */
  useEffect(() => {
    if (!componentsData?.assetsWithMetadata?.items) {
      return;
    }

    const initialItems = componentsData.assetsWithMetadata.items.filter(
      (a): a is AtlasGqlAsset => !!a
    );
    const totalCount = componentsData.assetsWithMetadata.totalCount;
    setAllComponents(initialItems);

    if (initialItems.length >= totalCount || initialItems.length >= COMPONENT_LIMIT) {
      return;
    }

    let cancelled = false;
    (async () => {
      let accumulated = initialItems;
      while (accumulated.length < totalCount && accumulated.length < COMPONENT_LIMIT) {
        const limit = Math.min(SUBSEQUENT_PAGE_SIZE, COMPONENT_LIMIT - accumulated.length);
        const result = await fetchMoreComponents({
          variables: { limit, offset: accumulated.length },
        });
        if (cancelled) {
          return;
        }
        const newItems = (result.data?.assetsWithMetadata?.items ?? []).filter(
          (a): a is AtlasGqlAsset => !!a
        );
        if (!newItems.length) {
          break;
        }
        accumulated = [...accumulated, ...newItems];
        setAllComponents(accumulated);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsData]);

  /**
   * Fetch make/model data when asset/component type is selected. This can be skipped if
   * site is selected, since the make/model data can be pulled from the returned assets.
   */
  useEffect(() => {
    if (!sitesSelected && !lockedAssetIds?.length && assetTypes?.length === 1) {
      if (assetTypes[0].name === 'Turbine') {
        getAssetMakeModelOptions({
          variables: {
            makeInput: {
              fieldName: 'turbine_make',
            },
            modelInput: {
              fieldName: 'turbine_model',
            },
            independentFieldNameForCombinations: 'turbine_make',
          },
        });
      } else if (assetTypes[0].name === 'Blade') {
        getAssetMakeModelOptions({
          variables: {
            makeInput: {
              fieldName: 'blade_make',
            },
            modelInput: {
              fieldName: 'blade_model',
            },
            independentFieldNameForCombinations: 'blade_make',
          },
        });
      }
    }
  }, [assetTypes]);

  useEffect(() => {
    if (!sitesSelected && componentTypes?.length === 1) {
      if (componentTypes[0].name === 'Blade') {
        getComponentMakeModelOptions({
          variables: {
            makeInput: {
              fieldName: 'blade_make',
            },
            modelInput: {
              fieldName: 'blade_model',
            },
            independentFieldNameForCombinations: 'blade_make',
          },
        });
      }
    }
  }, [componentTypes]);

  /**
   * Memoized sets of options for asset fields
   */
  const assetTypeOptions: AtlasGqlAssetType[] = useMemo(
    () => (assetTypesData?.assetTypes ?? []).filter(({ name }) => name !== 'Site'),
    [assetTypesData]
  );
  const assetMakeOptions = useMemo(() => {
    if (assetTypes?.length !== 1) {
      return [];
    }

    // Site selected: Makes from loaded Assets. Else: org Make options.
    if (allAssets.length) {
      return buildMakeOptionsFromAssets(
        allAssets.filter(a => assetMatchesAssetTypes(a, assetTypes)),
        NONE_OPTION
      );
    }

    if (assetMakeModelData) {
      return [
        NONE_OPTION,
        ...(assetMakeModelData.configuredMakes.existingValues ?? []).map(o => ({
          label: o,
          value: o,
        })),
      ];
    }

    return [];
  }, [allAssets, assetTypes, assetMakeModelData]);

  // Models by Make (nested UI only): site Assets when present, else org combinations ∩ configured models.
  const assetModelsByMake = useMemo(() => {
    if (!enableNestedMakeModelFilter) {
      return {};
    }

    if (assetTypes?.length !== 1 || !assetMakeOptions?.length) {
      return {};
    }

    if (allAssets.length) {
      return buildModelsByMakeFromAssets({
        assets: allAssets,
        assetTypeIds: assetTypes.map(({ id }) => id),
        assetTypeNames: assetTypes.map(({ name }) => name),
        makes: assetMakeOptions,
        includeNoneOption: NONE_OPTION,
      });
    }

    if (assetMakeModelData) {
      return buildModelsByMakeFromCombinations(
        assetMakeOptions,
        assetMakeModelData.assetFieldDefinition?.independentCombinationOptions,
        assetMakeModelData.configuredModels.existingValues,
        NONE_OPTION
      );
    }

    return {};
  }, [allAssets, assetTypes, assetMakeOptions, assetMakeModelData, enableNestedMakeModelFilter]);

  // Flat model options for legacy separate Make/Model selects.
  const assetModelOptions = useMemo(() => {
    if (enableNestedMakeModelFilter) {
      return [];
    }

    if (assetTypes?.length !== 1 || !assetMakes?.length) {
      return [];
    }

    if (allAssets.length) {
      return [
        NONE_OPTION,
        ...uniqBy(
          allAssets
            .filter(a => assetTypes.some(({ id }) => id === a.assetType?.id))
            .map(a => {
              const makeField: AtlasGqlAssetFieldValueSelect | null | undefined =
                a.fieldValues?.find(v => v?.fieldDefinition.name === 'Make');

              if (!makeField?.selectValue || !assetMakes.includes(makeField.selectValue)) {
                return undefined;
              }

              const modelField: AtlasGqlAssetFieldValueSelect | null | undefined =
                a.fieldValues?.find(v => v?.fieldDefinition.name === 'Model');

              return modelField?.selectValue ? { value: modelField.selectValue } : undefined;
            })
            .filter((v): v is { value: string } => !!v),
          'value'
        ),
      ];
    }

    if (assetMakeModelData) {
      return [
        NONE_OPTION,
        ...filterModelOptionsBySelectedMakes(
          assetMakeModelData.configuredModels.existingValues,
          assetMakes,
          assetMakeModelData.assetFieldDefinition.independentCombinationOptions
        ),
      ];
    }

    return [];
  }, [allAssets, assetTypes, assetMakes, assetMakeModelData, enableNestedMakeModelFilter]);

  const assetOptions: (AtlasGqlAsset & { displayName: string })[] = useMemo(() => {
    return filterAssetsByTypes(allAssets, assetTypes ?? [])
      .filter(a => {
        const makeField: AtlasGqlAssetFieldValueSelect | null | undefined = a.fieldValues?.find(
          v => v?.fieldDefinition.name === 'Make'
        );
        const modelField: AtlasGqlAssetFieldValueSelect | null | undefined = a.fieldValues?.find(
          v => v?.fieldDefinition.name === 'Model'
        );

        return matchesMakeModelSelection(makeField?.selectValue, modelField?.selectValue, {
          makes: assetMakes,
          models: assetModels,
          pairs: enableNestedMakeModelFilter ? assetMakeModelPairs : undefined,
        });
      })
      .map(asset => ({
        ...asset,
        displayName: getAssetColumnCopy({
          asset,
          includeSite: selectedSiteIds.length > 1,
          separatorText: ' | ',
        }),
      }))
      .sort(naturalSortBy('displayName'));
  }, [
    allAssets,
    assetTypes,
    assetMakes,
    assetModels,
    assetMakeModelPairs,
    enableNestedMakeModelFilter,
    selectedSiteIds.length,
  ]);

  /**
   * If the set of possible assets changes due to other filters, remove any previously
   * selected assets that are no longer available
   */
  useEffect(() => {
    const remainingAssets = assets?.filter(({ id }) => assetOptions.some(o => o.id === id));
    if (remainingAssets?.length !== assets?.length) {
      dispatchFilterState({ assets: remainingAssets?.length ? remainingAssets : undefined });
    }
  }, [assetOptions]);

  /**
   * Memoized sets of options for component fields
   */
  const componentTypeOptions: AtlasGqlAssetType[] = useMemo(
    () =>
      assetTypes?.some(({ name }) => name === 'Turbine')
        ? (assetTypesData?.assetTypes ?? []).filter(
            ({ name }) => !['Site', 'Turbine'].includes(name)
          )
        : [],
    [assetTypesData, assetTypes]
  );
  const componentMakeOptions = useMemo(() => {
    if (componentTypes?.length !== 1) {
      return [];
    }

    /**
     * If one or more sites has been filtered, pull component makes from the components of the selected
     * type at that site
     */
    if (allComponents.length) {
      return [
        NONE_OPTION,
        ...uniqBy(
          allComponents
            .filter(a => componentTypes.some(({ id }) => id === a.assetType?.id))
            .map(a => {
              const makeField: AtlasGqlAssetFieldValueSelect | null | undefined =
                a.fieldValues?.find(v => v?.fieldDefinition.name === 'Make');
              return makeField?.selectValue ? { value: makeField.selectValue } : undefined;
            })
            .filter((v): v is { value: string } => !!v),
          'value'
        ),
      ];
    }

    /**
     * If no sites have been filtered, the list of makes is those of the selected type that
     * exist in the organization
     */
    if (componentMakeModelData) {
      return [
        NONE_OPTION,
        ...(componentMakeModelData.configuredMakes.existingValues ?? []).map(o => ({
          label: o,
          value: o,
        })),
      ];
    }
  }, [allComponents, componentTypes, componentMakeModelData]);
  const componentModelOptions = useMemo(() => {
    if (componentTypes?.length !== 1 || !componentMakes?.length) {
      return [];
    }

    /**
     * If one or more sites has been filtered, pull component models from the components of the selected
     * type at that site
     */
    if (allComponents.length) {
      return [
        NONE_OPTION,
        ...uniqBy(
          allComponents
            .filter(a => componentTypes.some(({ id }) => id === a.assetType?.id))
            .map(a => {
              const makeField: AtlasGqlAssetFieldValueSelect | null | undefined =
                a.fieldValues?.find(v => v?.fieldDefinition.name === 'Make');

              if (!makeField?.selectValue || !componentMakes.includes(makeField.selectValue)) {
                return undefined;
              }

              const modelField: AtlasGqlAssetFieldValueSelect | null | undefined =
                a.fieldValues?.find(v => v?.fieldDefinition.name === 'Model');

              return modelField?.selectValue ? { value: modelField.selectValue } : undefined;
            })
            .filter((v): v is { value: string } => !!v),
          'value'
        ),
      ];
    }

    /**
     * If no sites have been filtered, the list of models is those of the selected type that
     * exist in the organization and are valid models of a selected make
     */
    if (componentMakeModelData) {
      return [
        NONE_OPTION,
        ...filterModelOptionsBySelectedMakes(
          componentMakeModelData.configuredModels.existingValues,
          componentMakes,
          componentMakeModelData.assetFieldDefinition.independentCombinationOptions
        ),
      ];
    }
  }, [allComponents, componentTypes, componentMakes, componentMakeModelData]);
  const componentOptions: (AtlasGqlAsset & { displayName: string })[] = useMemo(() => {
    return allComponents
      .filter(a => {
        // filter out assets that don't match selected type(s)
        if (componentTypes?.length && !componentTypes.some(t => t?.id === a.assetType?.id)) {
          return false;
        }

        // filter out assets that don't match selected make(s)
        if (componentMakes?.length) {
          const makeField: AtlasGqlAssetFieldValueSelect | null | undefined = a.fieldValues?.find(
            v => v?.fieldDefinition.name === 'Make'
          );

          if (!makeField || !componentMakes.some(m => m === makeField.selectValue)) {
            return false;
          }
        }

        // filter out assets that don't match selected model(s)
        if (componentModels?.length) {
          const modelField: AtlasGqlAssetFieldValueSelect | null | undefined = a.fieldValues?.find(
            v => v?.fieldDefinition.name === 'Model'
          );

          if (!modelField || !componentModels.some(m => m === modelField.selectValue)) {
            return false;
          }
        }

        return true;
      })
      .map(asset => ({
        ...asset,
        displayName: getAssetColumnCopy({
          asset,
          includeSite: selectedSiteIds.length > 1,
          separatorText: ' | ',
        }),
      }))
      .sort(naturalSortBy('displayName'));
  }, [allComponents, componentTypes, componentMakes, componentModels]);

  /**
   * If the set of possible components changes due to other filters, remove any previously
   * selected components that are no longer available
   */
  useEffect(() => {
    const remainingComponents = components?.filter(({ id }) =>
      componentOptions.some(o => o.id === id)
    );
    if (remainingComponents?.length !== components?.length) {
      dispatchFilterState({
        components: remainingComponents?.length ? remainingComponents : undefined,
      });
    }
  }, [componentOptions]);

  /**
   * Restore persisted state from localStorage and pre-populate locked asset types/ids (e.g. on the
   * asset details page damage table).
   * These values will already have been passed in the graphql query, but we want to restore the
   * state here so it can be visualized and updated.
   *
   * Since the fields are interdependent and reliant on different data being returned from the API,
   * there is a separate useEffect for each field (other than make/model, which can be grouped).
   */

  // Will be true when all values from persisted state have been reflected in the filter component
  useEffect(() => {
    // Skip client-only keys (e.g. pairs) — they are not in graphqlToStateKeyMap.
    const requiredKeyPairs = (
      selectedKeys as {
        key: string;
        value: (number | number[] | string)[] | string;
      }[]
    )
      .map(
        ({ key }) =>
          [key, graphqlToStateKeyMap[key as keyof typeof graphqlToStateKeyMap]] as [
            string,
            keyof StateToGraphqlKeyMap | undefined,
          ]
      )
      .filter((entry): entry is [string, keyof StateToGraphqlKeyMap] => Boolean(entry[1]));
    if (lockedAssetTypes?.length) {
      requiredKeyPairs.push([stateToGraphqlKeyMap['assetTypes'], 'assetTypes']);
    }
    if (lockedAssetIds?.length) {
      requiredKeyPairs.push([stateToGraphqlKeyMap['assets'], 'assets']);
    }
    const restored = requiredKeyPairs.every(
      ([graphqlKey, stateKey]) =>
        stateKey in filterState &&
        filterState[stateKey as keyof AssetFilterState]?.length ===
          (
            selectedKeys as {
              key: string;
              value: (number | number[] | string)[] | string;
            }[]
          ).find(({ key }) => key === graphqlKey)?.value?.length
    );
    if (restored) {
      setPersistedStateRestored(true);
    }
  }, [selectedKeys, filterState, lockedAssetIds, lockedAssetTypes]);

  // Nested: clear Make/Model when Site actually changes. Legacy: remount restore only (master).
  const previousSelectedSiteIdsRef = useRef<string[] | null | undefined>(null);
  useEffect(() => {
    if (!enableNestedMakeModelFilter) {
      setPersistedStateRestored(false);
      return;
    }

    const siteChanged = consumeSiteFilterChange(previousSelectedSiteIdsRef, selectedSiteIds);
    if (!siteChanged) {
      return;
    }
    setPersistedStateRestored(false);
    const makeModelKeys = makeModelFilterKeys(stateToGraphqlKeyMap);
    const tableStillHasMakeModel = (selectedKeys as { key: string }[]).some(({ key }) =>
      makeModelKeys.has(key)
    );
    // Saved views apply Site + Make/Model together; keep local state for restore.
    if (tableStillHasMakeModel) {
      return;
    }
    dispatchFilterState({
      assetMakes: undefined,
      assetModels: undefined,
      assetMakeModelPairs: undefined,
      assets: undefined,
      componentMakes: undefined,
      componentModels: undefined,
      components: undefined,
      ...(resetTypesOnSiteChange ? { assetTypes: undefined, componentTypes: undefined } : {}),
    });
  }, [
    selectedSiteIds,
    selectedKeys,
    enableNestedMakeModelFilter,
    resetTypesOnSiteChange,
    stateToGraphqlKeyMap,
  ]);

  const getPersistedFilter = useCallback(
    (key: keyof typeof graphqlToStateKeyMap): string[] | undefined =>
      (
        selectedKeys as {
          key: string;
          value: (number | number[] | string)[] | string;
        }[]
      ).find(filter => filter.key === key)?.value as string[] | undefined,
    [selectedKeys]
  );

  // Restore saved asset type(s)
  useEffect(() => {
    if (
      !persistedStateRestored &&
      (selectedKeys.length || lockedAssetTypes?.length) &&
      assetTypeOptions.length
    ) {
      const selectedAssetTypes = getPersistedFilter(stateToGraphqlKeyMap['assetTypes']);
      if (selectedAssetTypes) {
        dispatchFilterState({
          assetTypes: assetTypeOptions.filter(({ name }) =>
            selectedAssetTypes.some(t => t === snakeCase(name.toLowerCase()))
          ),
        });
      } else if (lockedAssetTypes?.length) {
        dispatchFilterState({
          assetTypes: assetTypeOptions.filter(({ id }) => lockedAssetTypes.some(t => t.id === id)),
        });
      }
    }
  }, [persistedStateRestored, selectedKeys, assetTypeOptions, lockedAssetTypes]);

  // Restore saved asset make(s)/model(s)
  useEffect(() => {
    if (!persistedStateRestored && selectedKeys.length && assetTypes?.length) {
      const filterState: AssetFilterState = {};
      const selectedAssetMakes = getPersistedFilter(stateToGraphqlKeyMap['assetMakes']);
      if (selectedAssetMakes) {
        filterState.assetMakes = selectedAssetMakes;
        const selectedAssetModels = getPersistedFilter(stateToGraphqlKeyMap['assetModels']);
        if (selectedAssetModels) {
          filterState.assetModels = selectedAssetModels;
        }
        if (enableNestedMakeModelFilter) {
          const selectedAssetPairs = getPersistedFilter(ASSET_MAKE_MODEL_PAIRS_KEY);
          if (selectedAssetPairs?.length) {
            filterState.assetMakeModelPairs = decodeMakeModelPairs(selectedAssetPairs);
          }
        }
        dispatchFilterState(filterState);
      }
    }
  }, [
    persistedStateRestored,
    selectedKeys,
    assetMakeOptions,
    assetModelsByMake,
    assetModelOptions,
    enableNestedMakeModelFilter,
  ]);

  // Restore saved asset(s)
  useEffect(() => {
    if (
      !persistedStateRestored &&
      (selectedKeys.length || lockedAssetIds?.length) &&
      assetOptions.length
    ) {
      const selectedAssetIdsWithSiteId = getPersistedFilter(stateToGraphqlKeyMap['assets']);
      if (selectedAssetIdsWithSiteId) {
        dispatchFilterState({
          assets: assetOptions.filter(({ id }) =>
            selectedAssetIdsWithSiteId.some(i => i.split(':')[0] === id)
          ),
        });
      } else if (lockedAssetIds?.length) {
        dispatchFilterState({
          assets: assetOptions.filter(({ id }) => lockedAssetIds.some(i => i === id)),
        });
      }
    }
  }, [persistedStateRestored, selectedKeys, lockedAssetIds, assetOptions]);

  // Restore saved component type(s)
  useEffect(() => {
    if (!persistedStateRestored && selectedKeys.length && componentTypeOptions.length) {
      const selectedComponentTypes = getPersistedFilter(stateToGraphqlKeyMap['componentTypes']);
      if (selectedComponentTypes) {
        dispatchFilterState({
          componentTypes: componentTypeOptions.filter(({ name }) =>
            selectedComponentTypes.some(t => t === snakeCase(name.toLowerCase()))
          ),
        });
      }
    }
  }, [persistedStateRestored, selectedKeys, componentTypeOptions]);

  // Restore saved component make(s)/model(s)
  useEffect(() => {
    if (!persistedStateRestored && selectedKeys.length) {
      const filterState: AssetFilterState = {};
      const selectedComponentMakes = getPersistedFilter(stateToGraphqlKeyMap['componentMakes']);
      if (selectedComponentMakes) {
        filterState.componentMakes = selectedComponentMakes;
        const selectedComponentModels = getPersistedFilter(stateToGraphqlKeyMap['componentModels']);
        if (selectedComponentModels) {
          filterState.componentModels = selectedComponentModels;
        }
        dispatchFilterState(filterState);
      }
    }
  }, [persistedStateRestored, selectedKeys, componentMakeOptions, componentModelOptions]);

  // Restore saved component(s)
  useEffect(() => {
    if (!persistedStateRestored && selectedKeys.length && componentOptions.length) {
      const selectedComponentIdsWithSiteId = getPersistedFilter(stateToGraphqlKeyMap['components']);
      if (selectedComponentIdsWithSiteId) {
        dispatchFilterState({
          components: componentOptions.filter(({ id }) =>
            selectedComponentIdsWithSiteId.some(i => i.split(':')[0] === id)
          ),
        });
      }
    }
  }, [persistedStateRestored, selectedKeys, componentOptions]);

  // If filters are cleared, reset working state in this component
  useEffect(() => {
    if (isEqual(selectedKeys, [])) {
      handleReset();
    }
  }, [selectedKeys]);

  // Clean up available options when the values they depend on change
  useEffect(() => {
    if ((!sitesSelected || !assetTypes?.length) && !lockedAssetIds?.length) {
      setAllAssets([]);
    }
  }, [sitesSelected, assetTypes, lockedAssetIds]);
  useEffect(() => {
    if (!assets?.length) {
      setAllComponents([]);
    }
  }, [assets]);

  // Keep Reset/OK visible when the filter grows near the viewport bottom.
  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover) {
      return;
    }

    const VIEWPORT_PADDING_PX = 16;

    const updateMaxHeight = () => {
      const { top } = popover.getBoundingClientRect();
      const available = window.innerHeight - top - VIEWPORT_PADDING_PX;
      const maxHeight = Math.max(160, Math.min(ASSET_FILTER_MAX_HEIGHT_PX, available));
      popover.style.maxHeight = `${maxHeight}px`;
    };

    updateMaxHeight();

    const resizeObserver = new ResizeObserver(updateMaxHeight);
    resizeObserver.observe(popover);
    window.addEventListener('resize', updateMaxHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, [assetMakeOptions, assetModelsByMake, assetModelOptions, assetTypes, isComponentCollapseOpen]);

  const handleSearch = (
    searchValue: string,
    option?: {
      label?: string | JSX.Element;
      value: string;
    }
  ) => {
    if (searchValue.length > 1) {
      const re = new RegExp(searchValue, 'i');
      return typeof option?.label === 'string'
        ? !!option.label.match(re)
        : !!option?.value.match(re);
    }
    return true;
  };

  const handleReset = () => {
    const keysToReset = Object.keys(filterState).filter(k => {
      if (!!lockedAssetTypes?.length && k === 'assetTypes') {
        return false;
      }
      if (!!lockedAssetIds?.length && k === 'assets') {
        return false;
      }
      return true;
    });

    dispatchFilterState(Object.fromEntries(keysToReset.map(k => [k, undefined])));
  };

  /**
   * Change handlers for state management
   */
  const handleChangeAssetType = (typeIds: string[]) => {
    const types = assetTypeOptions.filter(({ id }) => typeIds.includes(id));
    dispatchFilterState({
      assetTypes: !!types?.length ? types : undefined,
      assetMakes: undefined,
      assetModels: undefined,
      ...(enableNestedMakeModelFilter ? { assetMakeModelPairs: undefined } : {}),
      assets: retainSelectedAssetsForTypeChange({
        assets,
        assetTypes: types,
        hasLockedAssets: !!lockedAssetIds?.length,
      }),
      ...(!types?.length
        ? {
            componentTypes: undefined,
            componentMakes: undefined,
            componentModels: undefined,
          }
        : {}),
    });
  };
  const handleChangeAssetMake = (makes: string[]) => {
    dispatchFilterState({
      assetMakes: !!makes?.length ? makes : undefined,
      assetModels:
        !!assetMakeModelData && !!makes?.length
          ? filterModelOptionsBySelectedMakes(
              assetModels,
              makes,
              assetMakeModelData.assetFieldDefinition.independentCombinationOptions
            ).map(({ value }) => value)
          : undefined,
    });
  };
  const handleChangeAssetModel = (models: string[]) => {
    dispatchFilterState({ assetModels: !!models.length ? models : undefined });
  };
  const handleChangeAssetMakeModel = ({
    makes,
    models,
    pairs,
  }: {
    makes: string[];
    models: string[];
    pairs: SelectedMakeModel[];
  }) => {
    dispatchFilterState({
      assetMakes: makes.length ? makes : undefined,
      assetModels: models.length ? models : undefined,
      assetMakeModelPairs: pairs.length ? pairs : undefined,
    });
  };
  const handleChangeAsset = (assetIds: string[]) => {
    const assets = assetOptions.filter(({ id }) => assetIds.includes(id));
    dispatchFilterState({ assets: !!assets?.length ? assets : undefined });
  };
  const handleChangeComponentType = (typeIds: string[]) => {
    const types = componentTypeOptions.filter(({ id }) => typeIds.includes(id));
    dispatchFilterState({
      componentTypes: !!types?.length ? types : undefined,
      componentMakes: undefined,
      componentModels: undefined,
    });
  };
  const handleChangeComponentMake = (makes: string[]) => {
    dispatchFilterState({
      componentMakes: !!makes?.length ? makes : undefined,
      componentModels:
        !!componentMakeModelData && !!makes?.length
          ? filterModelOptionsBySelectedMakes(
              componentModels,
              makes,
              componentMakeModelData.assetFieldDefinition.independentCombinationOptions
            ).map(({ value }) => value)
          : undefined,
    });
  };
  const handleChangeComponentModel = (models: string[]) => {
    dispatchFilterState({ componentModels: !!models.length ? models : undefined });
  };
  const handleChangeComponent = (componentIds: string[]) => {
    const components = componentOptions.filter(({ id }) => componentIds.includes(id));
    dispatchFilterState({ components: !!components?.length ? components : undefined });
  };

  /**
   * Turn this component's state into antd table/graphql filter format
   */
  const handleSubmit = () => {
    const selectedKeys = [];

    // Locked asset types are already filtered at the top level, so we don't want to send them twice
    if (assetTypes && !lockedAssetTypes?.length) {
      selectedKeys.push({
        key: stateToGraphqlKeyMap['assetTypes'],
        value: assetTypes.map(({ name }) => snakeCase(name.toLowerCase())),
      });

      if (assetTypes.length === 1 && assetMakes) {
        selectedKeys.push({
          key: stateToGraphqlKeyMap['assetMakes'],
          value: assetMakes,
        });

        if (assetModels) {
          selectedKeys.push({
            key: stateToGraphqlKeyMap['assetModels'],
            value: assetModels,
          });
        }

        if (enableNestedMakeModelFilter && assetMakeModelPairs?.length) {
          selectedKeys.push({
            key: ASSET_MAKE_MODEL_PAIRS_KEY,
            value: encodeMakeModelPairs(assetMakeModelPairs),
          });
        }
      }
    }

    // Locked asset ids are already filtered at the top level, so we don't want to send them twice
    if (assets && !lockedAssetIds?.length) {
      selectedKeys.push({
        key: stateToGraphqlKeyMap['assets'],
        value: assets.map(({ id, ancestors }) => {
          const site = ancestors?.find(({ assetType }) => assetType?.name === 'Site');
          return `${id}:${site?.id}`;
        }),
      });
    }

    if (componentTypes) {
      selectedKeys.push({
        key: stateToGraphqlKeyMap['componentTypes'],
        value: componentTypes.map(({ name }) => snakeCase(name.toLowerCase())),
      });

      if (componentTypes.length === 1 && componentMakes) {
        selectedKeys.push({
          key: stateToGraphqlKeyMap['componentMakes'],
          value: componentMakes,
        });

        if (componentModels) {
          selectedKeys.push({
            key: stateToGraphqlKeyMap['componentModels'],
            value: componentModels,
          });
        }
      }
    }

    if (components) {
      selectedKeys.push({
        key: stateToGraphqlKeyMap['components'],
        value: components.map(({ id, ancestors }) => {
          const site = ancestors?.find(({ assetType }) => assetType?.name === 'Site');
          return `${id}:${site?.id}`;
        }),
      });
    }

    setSelectedKeys(selectedKeys);
    confirm({ closeDropdown: true });
  };

  return (
    <AutoFocus elementToFocus={inputRef}>
      <AssetFilterPopover ref={popoverRef} className="ant-table-filter-dropdown" $width="480px">
        <AssetFilterInnerContainer>
          {tooManyAssets ? (
            <AssetFilterWarning>
              {t('table.limitingAssets', {
                count: ASSET_LIMIT,
                defaultValue_one: 'Limiting Assets to the first {{count}} item',
                defaultValue_other: 'Limiting Assets to the first {{count}} items',
              })}
            </AssetFilterWarning>
          ) : null}
          <AssetSelectContainer>
            <span>{t('table.assetTypeLabel', 'Asset Type:')}</span>
            <TableFilterMultiSelect
              ref={inputRef}
              listHeight={160}
              options={assetTypeOptions.map(({ id, name }) => ({ value: id, label: name }))}
              onChange={handleChangeAssetType}
              filterOption={handleSearch}
              value={assetTypes?.map(({ id }) => id)}
              loading={assetTypesLoading}
              showSearch
              placeholder={
                assetTypesLoading
                  ? t('table.loading', 'Loading...')
                  : t('table.filterSelectAssetType', 'Select asset type')
              }
              disabled={!!lockedAssetTypes?.length || !!lockedAssetIds?.length}
              allowClear
              suffixIcon={searchSuffixIcon}
            />
          </AssetSelectContainer>
          {!lockedAssetIds?.length &&
            (enableNestedMakeModelFilter ? (
              <AssetSelectContainer>
                <span>{t('table.assetMakeModelLabel', 'Asset Make / Model:')}</span>
                <MakeModelNestedFilter
                  makes={assetMakeOptions ?? []}
                  modelsByMake={assetModelsByMake}
                  selectedMakes={assetMakes ?? []}
                  selectedModels={assetModels ?? []}
                  selectedPairs={assetMakeModelPairs}
                  onChange={handleChangeAssetMakeModel}
                  hasAssetTypeSelected={assetTypes?.length === 1}
                  disabled={assetTypes?.length !== 1 || !!lockedAssetTypes?.length}
                  loading={
                    assetTypes?.length === 1 &&
                    !assetMakeOptions?.length &&
                    (sitesSelected ? assetsLoading : assetMakeModelDataLoading)
                  }
                />
              </AssetSelectContainer>
            ) : (
              <>
                <AssetSelectContainer $selectWidth="260px">
                  <span>{t('table.assetMakeLabel', 'Asset Make:')}</span>
                  <TableFilterMultiSelect
                    listHeight={160}
                    options={assetMakeOptions}
                    onChange={handleChangeAssetMake}
                    filterOption={handleSearch}
                    value={assetMakes}
                    disabled={assetTypes?.length !== 1 || !!lockedAssetTypes?.length}
                    loading={assetMakeModelDataLoading}
                    showSearch
                    placeholder={
                      assetTypes?.length !== 1
                        ? undefined
                        : assetMakeModelDataLoading
                          ? t('table.loading', 'Loading...')
                          : t('table.filterSelectAssetMake', 'Select asset  make')
                    }
                    allowClear
                    suffixIcon={searchSuffixIcon}
                  />
                </AssetSelectContainer>
                <AssetSelectContainer $selectWidth="260px">
                  <span>{t('table.assetModelLabel', 'Asset Model:')}</span>
                  <TableFilterMultiSelect
                    listHeight={160}
                    options={assetModelOptions}
                    onChange={handleChangeAssetModel}
                    filterOption={handleSearch}
                    value={assetModels}
                    disabled={!assetMakes?.length || !!lockedAssetTypes?.length}
                    loading={assetMakeModelDataLoading}
                    showSearch
                    placeholder={
                      !assetMakes?.length
                        ? undefined
                        : assetMakeModelDataLoading
                          ? t('table.loading', 'Loading...')
                          : t('table.filterSelectAssetModel', 'Select asset model')
                    }
                    allowClear
                    suffixIcon={searchSuffixIcon}
                  />
                </AssetSelectContainer>
              </>
            ))}
          <AssetSelectContainer>
            <span>{t('images.assetLabel', 'Asset:')}</span>
            <Tooltip
              title={
                !!lockedAssetIds?.length
                  ? t(
                      'table.assetOnDamageTableTooltip',
                      'You are on the damage table for this asset'
                    )
                  : !(sitesSelected && assetTypes?.length)
                    ? !['site-damage-list-table2', 'site-inspections-list'].includes(tableId)
                      ? t(
                          'table.selectSiteAndAssetTypeToFilter',
                          'Please select at least one site in the site column filter and at least one asset type above to filter by asset'
                        )
                      : t(
                          'table.selectAssetTypeToFilter',
                          'Please select at least one asset type above to filter by asset'
                        )
                    : undefined
              }
            >
              <TableFilterMultiSelect
                listHeight={160}
                options={assetOptions.map(({ id, displayName }) => ({
                  value: id,
                  label: displayName,
                }))}
                onChange={handleChangeAsset}
                filterOption={handleSearch}
                value={assets?.map(({ id }) => id)}
                disabled={!(sitesSelected && assetTypes?.length) || !!lockedAssetIds?.length}
                loading={assetsLoading}
                showSearch
                placeholder={
                  !(sitesSelected && assetTypes?.length)
                    ? undefined
                    : assetsLoading
                      ? t('table.loadingAssets', 'Loading assets...')
                      : t('settings.pleaseSelect', 'Please select')
                }
                popupMatchSelectWidth={false}
                allowClear
                suffixIcon={searchSuffixIcon}
              />
            </Tooltip>
          </AssetSelectContainer>
          <ComponentFiltersCollapse
            style={{ marginRight: 0 }}
            defaultActiveKey={isComponentCollapseOpen ? [1] : []}
            onChange={keys =>
              setIsComponentCollapseOpen(Array.isArray(keys) ? keys.length > 0 : !!keys)
            }
            items={[
              {
                key: 1,
                label: (
                  <>
                    <span>{t('table.componentFilterOptions', 'Component Filter Options')}</span>{' '}
                    <TooltipWithQuestionIcon
                      message={t(
                        'table.componentFilterTooltip',
                        'When a parent asset is selected above, its child components may be filtered'
                      )}
                    />
                  </>
                ),
                children: (
                  <>
                    {tooManyComponents ? (
                      <AssetFilterWarning>
                        {t('table.limitingComponents', {
                          count: COMPONENT_LIMIT,
                          defaultValue_one: 'Limiting Components to the first {{count}} item',
                          defaultValue_other: 'Limiting Components to the first {{count}} items',
                        })}
                      </AssetFilterWarning>
                    ) : null}
                    <AssetSelectContainer>
                      <span>{t('table.componentTypeLabel', 'Component Type:')}</span>
                      <TableFilterMultiSelect
                        listHeight={160}
                        options={componentTypeOptions.map(({ id, name }) => ({
                          value: id,
                          label: name,
                        }))}
                        onChange={handleChangeComponentType}
                        filterOption={handleSearch}
                        value={componentTypes?.map(({ id }) => id)}
                        disabled={!componentTypeOptions?.length}
                        loading={assetTypesLoading}
                        showSearch
                        placeholder={
                          !componentTypeOptions?.length
                            ? undefined
                            : assetTypesLoading
                              ? t('table.loading', 'Loading...')
                              : t('table.filterSelectComponentType', 'Select component type')
                        }
                        allowClear
                        suffixIcon={searchSuffixIcon}
                      />
                    </AssetSelectContainer>
                    <AssetSelectContainer $selectWidth="260px">
                      <span>{t('table.componentMakeLabel', 'Component Make:')}</span>
                      <TableFilterMultiSelect
                        listHeight={160}
                        options={componentMakeOptions}
                        onChange={handleChangeComponentMake}
                        filterOption={handleSearch}
                        value={componentMakes}
                        disabled={componentTypes?.length !== 1}
                        loading={componentMakeModelDataLoading}
                        showSearch
                        placeholder={
                          componentTypes?.length !== 1
                            ? undefined
                            : componentMakeModelDataLoading
                              ? t('table.loading', 'Loading...')
                              : t('table.filterSelectComponentMake', 'Select component make')
                        }
                        allowClear
                        suffixIcon={searchSuffixIcon}
                      />
                    </AssetSelectContainer>
                    <AssetSelectContainer $selectWidth="260px">
                      <span>{t('table.componentModelLabel', 'Component Model:')}</span>
                      <TableFilterMultiSelect
                        listHeight={160}
                        options={componentModelOptions}
                        onChange={handleChangeComponentModel}
                        filterOption={handleSearch}
                        value={componentModels}
                        disabled={!componentMakes?.length}
                        loading={componentMakeModelDataLoading}
                        showSearch
                        placeholder={
                          !componentMakes?.length
                            ? undefined
                            : componentMakeModelDataLoading
                              ? t('table.loading', 'Loading...')
                              : t('table.filterSelectComponentModel', 'Select component model')
                        }
                        allowClear
                        suffixIcon={searchSuffixIcon}
                      />
                    </AssetSelectContainer>
                    <AssetSelectContainer style={{ marginBottom: 0 }}>
                      <span>{t('table.componentLabel', 'Component:')}</span>
                      <Tooltip
                        title={
                          !assets
                            ? t(
                                'table.selectAssetToFilterComponent',
                                'Please select at least one asset above to filter by component'
                              )
                            : undefined
                        }
                      >
                        <TableFilterMultiSelect
                          listHeight={160}
                          options={componentOptions.map(({ id, displayName }) => ({
                            value: id,
                            label: displayName,
                          }))}
                          onChange={handleChangeComponent}
                          filterOption={handleSearch}
                          value={components?.map(({ id }) => id)}
                          disabled={!assets || !componentOptions.length}
                          loading={componentsLoading}
                          showSearch
                          placeholder={
                            !assets || !componentOptions.length
                              ? undefined
                              : componentsLoading
                                ? t('table.loadingComponents', 'Loading components...')
                                : t('table.filterSelectComponent', 'Select component')
                          }
                          allowClear
                          suffixIcon={searchSuffixIcon}
                        />
                      </Tooltip>
                    </AssetSelectContainer>
                  </>
                ),
              },
            ]}
          />
        </AssetFilterInnerContainer>
        <AssetFilterActions>
          <Button
            type="link"
            size="small"
            onMouseDown={event =>
              handleTableFilterActionMouseDown(event, () => {
                // clearFilters only resets the draft; confirm applies it.
                clearFilters && clearFilters();
                confirm({ closeDropdown: true });
                handleReset();
              })
            }
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={event => handleTableFilterActionMouseDown(event, handleSubmit)}
            data-testid="asset-filter-ok-btn"
          >
            {t('table.ok', 'OK')}
          </Button>
        </AssetFilterActions>
      </AssetFilterPopover>
    </AutoFocus>
  );
};

/** Column filter wiring for nested Make/Model (WO Tasks, Logging Task logs). */
export const getNestedMakeModelAssetFilterColumnProps = (tableId: string) => ({
  allKeys: [...Object.values(DefaultStateToGraphqlKeyMap), ...MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS],
  clientOnlyFilterKeys: [...MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS],
  filterDropdown: (props: CustomFilterDropdownProps) => (
    <AssetFilter {...props} tableId={tableId} enableNestedMakeModelFilter />
  ),
});
